import { morphToolbar } from "../app/toolbarMorph.ts";
import {
  HIGHLIGHT_COLORS,
  HIGHLIGHT_LABELS,
  type HighlightColor,
} from "../model/highlights.ts";

/**
 * Marcador: se elige una herramienta —un color o la goma— y queda "en la mano"
 * hasta que se la suelta, así se puede ir marcando párrafo tras párrafo sin
 * volver a tocar nada. Es el modo natural para estudiar — el atajo de teclado
 * sirve para una marca suelta; esto sirve para una sesión de lectura entera.
 *
 * La goma hace falta desde que los colores se superponen: con marcas apiladas,
 * pasar el mismo fibrón encima ya no alcanza para deshacer una pasada suelta.
 *
 * Vive en la barra flotante del documento: el fibrón y la goma van en la fila
 * de controles, y los colores en la fila de opciones de esa misma barra, que
 * aparece recién cuando hay un fibrón en la mano. Así los colores no estiran la
 * fila de controles —que es lo que se usa todo el tiempo— pero tampoco se van a
 * un panel flotante encima del texto: quedan a la vista mientras marcás, y
 * cambiar de color es un clic.
 *
 * Componente tonto: sólo dibuja y avisa qué se eligió. Quién aplica (o borra) el
 * resaltado —y en qué modo tiene que estar la vista— es cosa de la vista.
 */

/** Lo que puede haber "en la mano": un color, o la goma. */
export type MarkerTool = HighlightColor | "eraser";

// Íconos de Material Symbols (Apache 2.0), variante de contorno: el fibrón es el
// `format_ink_highlighter` y la goma el `ink_eraser`. Van a 18px —una pizca más
// que los 17 de los íconos de trazo de la barra— porque tienen más aire adentro.
const FIBRON_ICON_SVG =
  '<svg viewBox="0 -960 960 960" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M80 0v-160h800V0H80Zm504-480L480-584 320-424l103 104 161-160Zm-47-160 103 103 160-159-104-104-159 160Zm-84-29 216 216-189 190q-24 24-56.5 24T367-263l-27 23H140l126-125q-24-24-25-57.5t23-57.5l189-189Zm0 0 187-187q24-24 56.5-24t56.5 24l104 103q24 24 24 56.5T857-640L669-453 453-669Z"/></svg>';
const ERASER_ICON_SVG =
  '<svg viewBox="0 -960 960 960" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M690-240h190v80H610l80-80Zm-500 80-85-85q-23-23-23.5-57t22.5-58l440-456q23-24 56.5-24t56.5 23l199 199q23 23 23 57t-23 57L520-160H190Zm296-80 314-322-198-198-442 456 64 64h262Zm-6-240Z"/></svg>';

export class MarkerBar {
  /** El fibrón y la goma, para la fila de controles de la barra. */
  readonly root: HTMLElement;
  /**
   * La fila de colores, para que la vista la monte en la zona de opciones de la
   * barra. Se muestra y se esconde sola desde `setActive`.
   */
  readonly options: HTMLElement;
  private buttons = new Map<MarkerTool, HTMLButtonElement>();
  private active: MarkerTool | null = null;
  /** Botón de fibrón: toma y suelta el marcador. */
  private trigger: HTMLButtonElement;
  /** El último color usado, para que el fibrón lo vuelva a tomar. */
  private lastColor: HighlightColor = HIGHLIGHT_COLORS[0];

  constructor(private onPick: (tool: MarkerTool | null) => void) {
    this.root = document.createElement("div");
    this.root.className = "hl-bar";

    // La fila de colores es una fila de opciones más: mismo espaciado que la
    // paleta del canvas.
    const tools = document.createElement("div");
    tools.className = "toolbar-row toolbar-colors hl-bar-swatches";
    for (const color of HIGHLIGHT_COLORS) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `hl-swatch hl-${color}`;
      b.title = `${HIGHLIGHT_LABELS[color]} — click to highlight, click again to drop`;
      b.setAttribute("aria-label", `${HIGHLIGHT_LABELS[color]} highlighter`);
      this.wireTool(b, color);
      tools.append(b);
    }

    // La goma va al lado del fibrón, no adentro del panel de colores: borrar no
    // es elegir un color, y tenerla afuera la deja a un clic — con las marcas
    // apiladas se borra tanto como se marca.
    const eraser = document.createElement("button");
    eraser.type = "button";
    eraser.className = "doc-tool";
    eraser.innerHTML = ERASER_ICON_SVG;
    eraser.title = "Eraser — select marked text to remove the top highlight";
    eraser.setAttribute("aria-label", "Eraser");
    this.wireTool(eraser, "eraser");

    // Los colores viven fuera de `root`: los monta la vista en la fila de
    // opciones de la barra, arriba de los controles.
    this.options = document.createElement("div");
    this.options.className = "toolbar-options";
    this.options.hidden = true;
    this.options.append(tools);

    this.trigger = document.createElement("button");
    this.trigger.type = "button";
    this.trigger.className = "doc-tool";
    this.trigger.innerHTML = FIBRON_ICON_SVG;
    this.trigger.setAttribute("aria-pressed", "false");
    this.trigger.addEventListener("pointerdown", (e) => e.preventDefault());
    // El fibrón toma y suelta: sin nada en la mano agarra el último color usado,
    // y con un color agarrado lo suelta. Un solo botón, un solo gesto.
    this.trigger.addEventListener("click", () => {
      const next = this.color === null ? this.lastColor : null;
      this.setActive(next);
      this.onPick(this.active);
    });

    this.root.append(this.trigger, eraser);
    this.setActive(null);
  }

  /** Color en uso, o null si hay una goma o nada en la mano. */
  get color(): HighlightColor | null {
    return this.active === "eraser" ? null : this.active;
  }

  /** ¿Está la goma en la mano? */
  get erasing(): boolean {
    return this.active === "eraser";
  }

  /** Lo que hay en la mano, sea color o goma. */
  get tool(): MarkerTool | null {
    return this.active;
  }

  /** Cambia el estado sin disparar `onPick` (para que lo apague quien lo mande). */
  setActive(tool: MarkerTool | null): void {
    this.active = tool;
    for (const [t, b] of this.buttons) {
      const on = t === tool;
      // La goma es un botón de la barra como cualquier otro y se enciende con
      // `active`, igual que el fibrón o los modos de lectura. Las pastillas de
      // color no son botones de la barra, así que llevan el suyo.
      b.classList.toggle(t === "eraser" ? "active" : "is-active", on);
      b.setAttribute("aria-pressed", String(on));
    }

    // El fibrón se enciende con un color en la mano; cuál es lo dice la pastilla
    // marcada de la fila de colores (y el título del botón). La goma tiene su
    // propio botón al lado y se enciende sola, con el bucle de arriba.
    const color = tool === "eraser" ? null : tool;
    if (color) this.lastColor = color;
    this.trigger.classList.toggle("active", color !== null);
    this.trigger.setAttribute("aria-pressed", String(color !== null));
    this.trigger.title = color
      ? `Highlighter: ${HIGHLIGHT_LABELS[color]}`
      : "Highlighter";

    // Los colores se ofrecen mientras haya un fibrón en la mano: son las
    // opciones de lo que estás usando, igual que las formas en el canvas.
    // El panel crece y encoge animado: quien lo hace es la barra entera (los
    // colores son una fila suya), así que el cambio se aplica adentro del morph.
    const bar = this.options.closest<HTMLElement>(".doc-toolbar");
    const hide = color === null;
    if (bar) morphToolbar(bar, () => (this.options.hidden = hide));
    else this.options.hidden = hide;
  }

  private wireTool(b: HTMLButtonElement, tool: MarkerTool): void {
    b.setAttribute("aria-pressed", "false");
    b.addEventListener("click", () => {
      // Clic sobre lo que ya está activo = soltarlo.
      this.setActive(this.active === tool ? null : tool);
      this.onPick(this.active);
      // La fila de colores no se cierra al elegir: está docked, no tapa nada, y
      // cambiar de color en mitad de una lectura es un clic y no tres.
    });
    this.buttons.set(tool, b);
  }
}
